import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import '@aws-amplify/ui-react/styles.css';
import { AnimatePresence } from 'framer-motion';
import { startAuthMonitor, stopAuthMonitor } from './utils/authMonitor';

// Pages
import Dashboard from './pages/Dashboard';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Landing from './pages/Landing';
import AdminDashboard from './pages/AdminDashboard';
import AdminLogin from './pages/AdminLogin';
import AdminForgotPassword from './pages/AdminForgotPassword';
import PhoneNumberInventory from './pages/PhoneNumberInventory';
import Customers from './pages/Customers';
import Appointments from './pages/Appointments';
import Queue from './pages/Queue';
import QueueAuth from './pages/QueueAuth';
import Messages from './pages/Messages';
import AgentConfig from './pages/AgentConfig';
import Analytics from './pages/Analytics';
import Settings from './pages/Settings';
import Menu from './pages/Menu';
import Rewards from './pages/Rewards';
import PaymentPage from './pages/PaymentPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';

// Layouts
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute'; 
import { BusinessProvider } from './contexts/BusinessContext';

// Theme configuration
const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#0066FF',
      dark: '#0052CC',
      light: '#00D4FF',
    },
    secondary: {
      main: '#FF6B6B',
    },
    background: {
      default: '#0a0e27', // Dark navy from landing page
      paper: '#14192d',   // Slightly lighter navy
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
  },
});

const AnimatedRoutes = () => {
  const location = useLocation();
  
  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        {/* Public Routes */}
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/pay/:paymentId" element={<PaymentPage />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />

        {/* Protected Vendor Routes */}
        <Route
          path="/dashboard"
          element={
            <BusinessProvider>
              <ProtectedRoute>
                {({ signOut, user }) => (
                  <Layout user={user} signOut={signOut}>
                    <Dashboard />
                  </Layout>
                )}
              </ProtectedRoute>
            </BusinessProvider>
          }
        />
        <Route
          path="/customers"
          element={
            <BusinessProvider>
              <ProtectedRoute>
                {({ signOut, user }) => (
                  <Layout user={user} signOut={signOut}>
                    <Customers />
                  </Layout>
                )}
              </ProtectedRoute>
            </BusinessProvider>
          }
        />
        <Route
          path="/queue"
          element={
            <BusinessProvider>
              <ProtectedRoute>
                {({ signOut, user }) => (
                  <Layout user={user} signOut={signOut}>
                    <Queue />
                  </Layout>
                )}
              </ProtectedRoute>
            </BusinessProvider>
          }
        />
        <Route
          path="/queue/:vendorId"
          element={<Queue />}
        />
        <Route
          path="/queue/:vendorId/auth"
          element={<QueueAuth />}
        />
        <Route
          path="/appointments"
          element={
            <BusinessProvider>
              <ProtectedRoute>
                {({ signOut, user }) => (
                  <Layout user={user} signOut={signOut}>
                    <Appointments />
                  </Layout>
                )}
              </ProtectedRoute>
            </BusinessProvider>
          }
        />
        <Route
          path="/messages"
          element={
            <BusinessProvider>
              <ProtectedRoute>
                {({ signOut, user }) => (
                  <Layout user={user} signOut={signOut}>
                    <Messages />
                  </Layout>
                )}
              </ProtectedRoute>
            </BusinessProvider>
          }
        />
        <Route
          path="/agent-config"
          element={
            <BusinessProvider>
              <ProtectedRoute>
                {({ signOut, user }) => (
                  <Layout user={user} signOut={signOut}>
                    <AgentConfig />
                  </Layout>
                )}
              </ProtectedRoute>
            </BusinessProvider>
          }
        />
        <Route
          path="/analytics"
          element={
            <BusinessProvider>
              <ProtectedRoute>
                {({ signOut, user }) => (
                  <Layout user={user} signOut={signOut}>
                    <Analytics />
                  </Layout>
                )}
              </ProtectedRoute>
            </BusinessProvider>
          }
        />
        <Route
          path="/settings"
          element={
            <BusinessProvider>
              <ProtectedRoute>
                {({ signOut, user }) => (
                  <Layout user={user} signOut={signOut}>
                    <Settings />
                  </Layout>
                )}
              </ProtectedRoute>
            </BusinessProvider>
          }
        />
        <Route
          path="/menu"
          element={
            <BusinessProvider>
              <ProtectedRoute>
                {({ signOut, user }) => (
                  <Layout user={user} signOut={signOut}>
                    <Menu />
                  </Layout>
                )}
              </ProtectedRoute>
            </BusinessProvider>
          }
        />
        <Route
          path="/rewards"
          element={
            <BusinessProvider>
              <ProtectedRoute>
                {({ signOut, user }) => (
                  <Layout user={user} signOut={signOut}>
                    <Rewards />
                  </Layout>
                )}
              </ProtectedRoute>
            </BusinessProvider>
          }
        />

        {/* Mission Control Routes (Internal Only) */}
        <Route path="/missioncontrol/login" element={<AdminLogin />} />
        <Route path="/missioncontrol/forgot-password" element={<AdminForgotPassword />} />
        <Route
          path="/missioncontrol"
          element={
            <BusinessProvider>
              <ProtectedRoute redirectTo="/missioncontrol/login" isAdminRoute={true}>
                {({ signOut, user }) => (
                  <Layout user={user} signOut={signOut} isAdminRoute={true}>
                    <AdminDashboard />
                  </Layout>
                )}
              </ProtectedRoute>
            </BusinessProvider>
          }
        />
        <Route
          path="/missioncontrol/phone-numbers"
          element={
            <BusinessProvider>
              <ProtectedRoute redirectTo="/missioncontrol/login" isAdminRoute={true}>
                {({ signOut, user }) => (
                  <Layout user={user} signOut={signOut} isAdminRoute={true}>
                    <PhoneNumberInventory />
                  </Layout>
                )}
              </ProtectedRoute>
            </BusinessProvider>
          }
        />
        {/* Redirect old routes to new mission control routes */}
        <Route path="/admin" element={<Navigate to="/missioncontrol" replace />} />
        <Route path="/admin/login" element={<Navigate to="/missioncontrol/login" replace />} />
        <Route path="/admin/phone-numbers" element={<Navigate to="/missioncontrol/phone-numbers" replace />} />
        <Route path="/admin/dashboard" element={<Navigate to="/missioncontrol" replace />} />

        {/* Catch-all redirect */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AnimatePresence>
  );
};

const App: React.FC = () => {
  useEffect(() => {
    // Start monitoring auth session for expiration
    startAuthMonitor();
    
    return () => {
      // Cleanup on unmount
      stopAuthMonitor();
    };
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AnimatedRoutes />
      </Router>
    </ThemeProvider>
  );
};

export default App;
